import { useState, useEffect } from "react";

/**
 * Forces a component to re-render at a specified interval while active.
 *
 * @param {number} intervalMs - The interval duration in milliseconds.
 * @param {boolean} isActive - Whether the interval should be running.
 *
 * This hook updates an internal state value every `intervalMs` milliseconds
 * to trigger a re-render. When `isActive` is `false`, the interval is cleared.
 *
 * Example usage:
 * ```tsx
 * useForceRenderAtInterval(60000, true);
 * ```
 */
const useForceRenderAtInterval = (
    intervalMs: number,
    isActive: boolean
): void => {
    const [, setTick] = useState(0);

    useEffect(() => {
        if (!isActive) return;

        const interval = setInterval(() => {
            setTick((t) => t + 1); // Increment state to trigger re-render
        }, intervalMs);

        return () => clearInterval(interval); // Cleanup on unmount or when inactive
    }, [intervalMs, isActive]);
};

export default useForceRenderAtInterval;
